import { useState } from "react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { CalendarIcon, Clock, ChevronUp, ChevronDown } from "lucide-react";
import { da } from "date-fns/locale";

// ─── Date-only picker ───
export const DatePicker = ({ value, onChange, label, required, placeholder, testId, minDate, error }) => {
  const [open, setOpen] = useState(false);
  const currentDate = value ? new Date(value + "T00:00:00") : null;

  const formatDisplay = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    if (isNaN(d)) return "";
    return d.toLocaleDateString("da-DK", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  };

  const handleSelect = (day) => {
    if (!day) return;
    const yr = day.getFullYear();
    const mo = String(day.getMonth() + 1).padStart(2, "0");
    const dy = String(day.getDate()).padStart(2, "0");
    onChange(`${yr}-${mo}-${dy}`);
    setOpen(false);
  };

  const disabledDays = minDate ? { before: new Date(minDate + "T00:00:00") } : undefined;

  return (
    <div>
      {label && <label className="block text-white mb-2 drop-shadow font-medium">{label} {required && "*"}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={`mc-input w-full text-left flex items-center gap-3 px-4 py-3 ${error ? 'border-red-500' : ''}`} data-testid={testId}>
            <CalendarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
            {value ? <span className="text-white">{formatDisplay(value)}</span> : <span className="text-gray-500">{placeholder || "Vælg dato"}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#0f0f12] border border-gray-700" align="start" sideOffset={8}>
          <Calendar
            mode="single"
            selected={currentDate}
            onSelect={handleSelect}
            locale={da}
            disabled={disabledDays}
            classNames={{
              months: "flex flex-col",
              month: "space-y-3",
              caption: "flex justify-center pt-1 relative items-center",
              caption_label: "text-sm font-medium text-white",
              nav: "space-x-1 flex items-center",
              nav_button: "h-7 w-7 bg-transparent border border-gray-700 rounded p-0 text-gray-400 hover:text-white hover:border-gray-500 inline-flex items-center justify-center",
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              table: "w-full border-collapse",
              head_row: "flex",
              head_cell: "text-gray-500 rounded-md w-9 font-normal text-[0.8rem]",
              row: "flex w-full mt-1",
              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#00d4ff]/20 [&:has([aria-selected])]:rounded-md",
              day: "h-9 w-9 p-0 font-normal text-gray-300 hover:bg-gray-700 rounded-md inline-flex items-center justify-center cursor-pointer",
              day_selected: "bg-[#00d4ff] text-black hover:bg-[#00d4ff] hover:text-black font-bold",
              day_today: "bg-gray-800 text-white font-bold",
              day_outside: "text-gray-600",
              day_disabled: "text-gray-700 opacity-40 cursor-not-allowed",
            }}
          />
        </PopoverContent>
      </Popover>
      {error && <p className="text-red-400 text-sm mt-1">{error}</p>}
    </div>
  );
};

// ─── Time-only picker ───
const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const mins = ["00", "15", "30", "45"];

export const TimePicker = ({ value, onChange, label, required, testId, error }) => {
  const [open, setOpen] = useState(false);
  const [h, m] = (value || "12:00").split(":").map(Number);

  const set = (hh, mm) => {
    onChange(`${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
  };

  return (
    <div>
      {label && <label className="block text-white mb-2 drop-shadow font-medium">{label} {required && "*"}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={`mc-input w-full text-left flex items-center gap-3 px-4 py-3 ${error ? 'border-red-500' : ''}`} data-testid={testId}>
            <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
            {value ? <span className="text-[#00d4ff] font-mono">{value}</span> : <span className="text-gray-500">Vælg tid</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3 bg-[#0f0f12] border border-gray-700" align="start" sideOffset={8}>
          <div className="flex items-center gap-2 justify-center">
            <div className="flex flex-col items-center">
              <button type="button" onClick={() => set(((h + 1) % 24 + 24) % 24, m)} className="p-1 text-gray-400 hover:text-white"><ChevronUp size={16} /></button>
              <div className="h-[140px] w-12 overflow-y-auto flex flex-col items-center" style={{ scrollbarWidth: "none" }}>
                {hours.map((hr) => (
                  <button key={hr} type="button" onClick={() => { set(Number(hr), m); }}
                    className={`w-full py-1.5 text-center text-sm rounded transition-all ${Number(hr) === h ? "bg-[#00d4ff]/20 text-[#00d4ff] font-bold" : "text-gray-500 hover:text-white"}`}>
                    {hr}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => set(((h - 1) % 24 + 24) % 24, m)} className="p-1 text-gray-400 hover:text-white"><ChevronDown size={16} /></button>
            </div>
            <span className="text-2xl font-bold text-white">:</span>
            <div className="flex flex-col items-center gap-0.5">
              <button type="button" onClick={() => set(h, (m + 15) % 60)} className="p-1 text-gray-400 hover:text-white"><ChevronUp size={16} /></button>
              {mins.map((mn) => (
                <button key={mn} type="button" onClick={() => { set(h, Number(mn)); }}
                  className={`w-12 py-1.5 text-center text-sm rounded transition-all ${Number(mn) === m ? "bg-[#00d4ff]/20 text-[#00d4ff] font-bold" : "text-gray-500 hover:text-white"}`}>
                  {mn}
                </button>
              ))}
              <button type="button" onClick={() => set(h, ((m - 15) % 60 + 60) % 60)} className="p-1 text-gray-400 hover:text-white"><ChevronDown size={16} /></button>
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-1 text-sm bg-[#00d4ff]/20 text-[#00d4ff] hover:bg-[#00d4ff]/30 rounded transition-colors">OK</button>
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="text-red-400 text-sm mt-1">{error}</p>}
    </div>
  );
};
